import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { ContactImport } from '@/components/message-drafts/contact-import'
import { ContactSelection } from '@/components/message-drafts/contact-selection'
import { MessageComposer } from '@/components/message-drafts/message-composer'
import { SendFlow } from '@/components/message-drafts/send-flow'
import { Button } from '@/components/ui/button'
import { ArrowLeft, MessageSquare } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { APP_NAME, APP_VERSION } from '@/lib/constants'
import { useAuth } from '@/hooks/use-auth'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export const Route = createFileRoute('/_public/app/')({
  component: AppPage,
})

export interface Contact {
  id: string
  name: string
  phone?: string
  email?: string
}

export interface SelectedContact extends Contact {
  sendSms: boolean
  sendEmail: boolean
}

type Step = 'import' | 'select' | 'compose' | 'send'

function AppPage() {
  const { currentUser, signOut } = useAuth()
  const [step, setStep] = useState<Step>('import')
  const [contacts, setContacts] = useState<Contact[]>([])
  const [selectedContacts, setSelectedContacts] = useState<SelectedContact[]>([])
  const [message, setMessage] = useState('')
  const [smsMessage, setSmsMessage] = useState('')
  const [subject, setSubject] = useState('')
  const [signature, setSignature] = useState('')

  const handleImportComplete = (importedContacts: Contact[]) => {
    setContacts(importedContacts)
    setStep('select')
  }

  const handleSelectionComplete = (selected: SelectedContact[]) => {
    setSelectedContacts(selected)
    setStep('compose')
  }

  const handleComposeComplete = (payload: {
    message: string
    subject: string
    smsMessage: string
    signature: string
  }) => {
    setMessage(payload.message)
    setSubject(payload.subject)
    setSmsMessage(payload.smsMessage)
    setSignature(payload.signature)
    setStep('send')
  }

  const handleBack = () => {
    if (step === 'select') setStep('import')
    else if (step === 'compose') setStep('select')
    else if (step === 'send') setStep('compose')
  }

  const handleStartOver = () => {
    setStep('import')
    setContacts([])
    setSelectedContacts([])
    setMessage('')
    setSmsMessage('')
    setSubject('')
    setSignature('')
  }

  const handleSendAnotherMessage = () => {
    setMessage('')
    setSmsMessage('')
    setSubject('')
    setStep('compose')
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-slate-900" />
            <span className="font-semibold text-xl">{APP_NAME}</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/app/help"
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              Help
            </Link>
            {currentUser ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="rounded-full border border-slate-300 px-3 py-1 text-sm text-slate-700 hover:border-slate-400 hover:text-slate-900"
                  >
                    {currentUser.email}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      void signOut()
                    }}
                  >
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/sign-in"
                className="text-sm text-slate-600 hover:text-slate-900"
              >
                Sign In
              </Link>
            )}
            {step !== 'import' && (
              <Button variant="ghost" size="sm" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Progress Indicator */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-center gap-2 text-sm">
            <StepIndicator
              number={1}
              label="Import"
              active={step === 'import'}
              completed={['select', 'compose', 'send'].includes(step)}
            />
            <div className="w-8 h-px bg-slate-300" />
            <StepIndicator
              number={2}
              label="Select"
              active={step === 'select'}
              completed={['compose', 'send'].includes(step)}
            />
            <div className="w-8 h-px bg-slate-300" />
            <StepIndicator
              number={3}
              label="Compose"
              active={step === 'compose'}
              completed={step === 'send'}
            />
            <div className="w-8 h-px bg-slate-300" />
            <StepIndicator number={4} label="Send" active={step === 'send'} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {step === 'import' && (
          <ContactImport onComplete={handleImportComplete} />
        )}
        {step === 'select' && (
          <ContactSelection
            contacts={contacts}
            onComplete={handleSelectionComplete}
          />
        )}
        {step === 'compose' && (
          <MessageComposer
            selectedContacts={selectedContacts}
            initialMessage={message}
            initialSubject={subject}
            initialSmsMessage={smsMessage}
            initialSignature={signature}
            onComplete={handleComposeComplete}
          />
        )}
        {step === 'send' && (
          <SendFlow
            contacts={selectedContacts}
            message={message}
            smsMessage={smsMessage}
            subject={subject}
            signature={signature}
            onStartOver={handleStartOver}
            onSendAnother={handleSendAnotherMessage}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container mx-auto px-4 text-center text-slate-600 text-sm">
          <p>© 2026 {APP_NAME} - v{APP_VERSION}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}

function StepIndicator({
  number,
  label,
  active,
  completed,
}: {
  number: number
  label: string
  active?: boolean
  completed?: boolean
}) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
          completed
            ? 'bg-slate-900 text-white'
            : active
              ? 'bg-slate-900 text-white'
              : 'bg-slate-200 text-slate-500'
        }`}
      >
        {number}
      </div>
      <span
        className={`hidden sm:inline ${active ? 'font-semibold' : 'text-slate-600'}`}
      >
        {label}
      </span>
    </div>
  )
}
